import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useMediaQuery } from 'react-responsive'
import { DataTable } from 'shared/components/DataTable/DataTable'
import CheckBox from 'shared/ui/CheckBox/CheckBox'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { SortableHeaderCell } from 'shared/components/DataTable/components/SortableHeaderCell/SortableHeaderCell'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'
import s from './styles/TableView.module.scss'
import { useStudentsDataTableColumns } from './hooks/useStudentsDataTableColumns'

const SKELETON_ROW_COUNT = 8
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => ({
	id: `skeleton-${i}`,
	name: '',
	grade: '',
	country: '',
	course: '',
	counselor: '',
	nearest_deadline: null,
}))

const TableView = observer(
	({
		students = [],
		selectedIds = [],
		allSelected = false,
		onToggleAll,
		onToggleOne,
		sortKey,
		sortDir,
		onSort,
		isOpenFilter,
		hasSelection,
		onSortChange,
		isLoading = false,
	}) => {
		const isMobile = useMediaQuery({ query: '(max-width: 1199px)' })

		const {
			currentCol,
			getSortOptions,
			getRowClickHandler,
			getCountriesMeta,
			getStatusMeta,
		} = useStudentsDataTableColumns({
			isMobile,
			onToggleOne,
		})

		const displayRows = useMemo(
			() => (isLoading && students.length === 0 ? SKELETON_ROWS : students),
			[isLoading, students],
		)

		const columns = useMemo(() => {
			return currentCol
				.map(c => {
					if (!c || !c?.id) return null

					if (c.id === 'select') {
						return {
							id: 'select',
							label: '',
							width: isMobile ? '38px' : '44px',
							sortable: false,
							headerClassName: s.colSelectHeader,
							cellClassName: s.colSelectCell,
							headerRender: () => (
								<CheckBox
									isSelected={allSelected}
									onToggle={onToggleAll}
									className={s.checkbox}
								/>
							),
							render: row => (
								<div
									className={`${s.cellClickStop} ${isLoading ? s.loading : ''}`}
									onClick={e => e.stopPropagation()}
									onMouseDown={e => e.stopPropagation()}
								>
									<CheckBox
										isSelected={selectedIds.includes(row?.id)}
										onToggle={() => onToggleOne?.(row?.id)}
										className={s.checkbox}
									/>
								</div>
							),
						}
					}

					if (c.id === 'student') {
						return {
							id: 'student',
							label: c.label,
							stickyLeft: 0,
							width: c.width,
							sortable: true,
							isSortActive: sortKey === c.sortableKey,
							sortDir,
							sortOptions: getSortOptions(c.sortableKey),
							onSortSelect: opt => {
								onSort?.(opt.key, opt.dir)
								onSortChange?.({ opt, name: c.label })
							},
							headerClassName: s.colStudentHeader,
							cellClassName: s.colStudentCell,
							render: row => (
								<div className={s.student}>
									{isLoading ? (
										<span className={s.student__avatarLoader}>
											<LoaderImg />
										</span>
									) : (
										<InitialAvatar
											name={row?.name}
											size={isMobile ? 25 : 39}
											avatarKey={row?.avatarKey}
											avatarSource={row?.avatarSource}
											photoUrl={row?.photoUrl}
										/>
									)}
									<span
										className={`${s.student__name} ${isLoading ? s.loaderText : ''}`}
									>
										{isLoading ? <LoaderImg /> : row?.name || '—'}
									</span>
								</div>
							),
						}
					}

					if (c.id === 'country') {
						return {
							id: 'country',
							label: c.label,
							width: c.width,
							sortable: true,
							isSortActive: sortKey === c.sortableKey,
							sortDir,
							sortOptions: getSortOptions(c.sortableKey),
							onSortSelect: opt => {
								onSort?.(opt.key, opt.dir)
								onSortChange?.({ opt, name: c.label })
							},
							headerClassName: s.colCountryHeader,
							cellClassName: s.colCountryCell,
							render: row => {
								if (isLoading) {
									return (
										<div className={s.country}>
											<span className={s.loaderText}>
												<LoaderImg />
											</span>
										</div>
									)
								}

								const { items, moreCount } = getCountriesMeta(row)

								if (!items.length) {
									return (
										<div className={s.country}>
											<span className={s.countryName}>—</span>
										</div>
									)
								}

								return (
									<div className={s.countryList}>
										{items.map(item => {
											const FlagIcon = item.FlagIcon
											return (
												<span key={item.code} className={s.country}>
													{FlagIcon ? (
														<span className={s.flag} aria-hidden="true">
															<FlagIcon />
														</span>
													) : null}
													<span className={s.countryName}>{item.label}</span>
												</span>
											)
										})}
										{moreCount > 0 ? (
											<span className={s.countryMore}>+{moreCount}</span>
										) : null}
									</div>
								)
							},
						}
					}

					const sortable = !!c.sortableKey
					return {
						id: c.id,
						label: c.label,
						width: c.width,
						sortable,
						isSortActive: sortable ? sortKey === c.sortableKey : false,
						sortDir,
						sortOptions: sortable ? getSortOptions(c.sortableKey) : [],
						onSortSelect: sortable
							? opt => {
									onSort?.(opt.key, opt.dir)
									onSortChange?.({ opt, name: c.label })
								}
							: undefined,
						headerClassName: s[`col_${c.id}_Header`] || '',
						cellClassName: s[`col_${c.id}_Cell`] || '',
						render: row =>
							isLoading ? (
								<div className={s.loaderText}>
									<LoaderImg />
								</div>
							) : (
								(c.getValue(row) ?? '—')
							),
					}
				})
				.filter(Boolean)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			currentCol,
			isMobile,
			selectedIds,
			allSelected,
			onSortChange,
			onToggleAll,
			onToggleOne,
			sortKey,
			sortDir,
			onSort,
			getSortOptions,
			getCountriesMeta,
			getStatusMeta,
			isLoading,
		])

		return (
			<section
				className={[
					s.section,
					isOpenFilter ? s.withFilter : '',
					hasSelection ? s.withSelection : '',
				]
					.filter(Boolean)
					.join(' ')}
			>
				{!!columns?.length && (
					<div
						className={[s.tableWrap, isLoading ? s.tableWrapLoading : '']
							.filter(Boolean)
							.join(' ')}
					>
						<DataTable
							className={s.wrap}
							shellClassName={s.shell}
							tableClassName={s.table}
							columns={columns}
							rows={displayRows}
							getRowId={r => r.id}
							stickyHeader={true}
							layout={isMobile ? 'fixed' : 'auto'}
							minWidth={isMobile ? 740 : 1100}
							emptyText={isLoading ? '' : 'No students'}
							headerCellComponent={SortableHeaderCell}
							onRowClick={getRowClickHandler()}
							isRowClickable={!isLoading}
							variant={'light'}
						/>
					</div>
				)}
			</section>
		)
	},
)

export default TableView
